import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import defaultLogo from "../../../../assets/img/auth/logo.jpg"; // ✅ fallback logo
import { API_BASE_URL } from "../../../../config";

const BillView = ({ billId, onClose }) => {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState({});
  const [project, setProject] = useState({});
  const [admin, setAdmin] = useState({});
  const [generatedByAdmin, setGeneratedByAdmin] = useState(null); // ✅ store "Generated By"
  const [logoUrl, setLogoUrl] = useState(defaultLogo);

  const token = localStorage.getItem("token");

  // 🔹 Fetch Main Admin details (company info + logo)
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/admin/get-admin`, {
        headers: { token },
      })
      .then((res) => {
        if (res.data.success) {
          setAdmin(res.data.result.admin || {});
        }
      })
      .catch(() => {});

    axios
      .get(`${API_BASE_URL}/api/admin/get-companylogo`, {
        headers: { token },
      })
      .then((res) => {
        const photoPath = res.data?.result?.profilePhoto;
        if (photoPath) {
          let finalPath = photoPath.startsWith("http")
            ? photoPath
            : `${API_BASE_URL}${
                photoPath.startsWith("/") ? photoPath : `/${photoPath}`
              }`;
          setLogoUrl(finalPath);
        }
      })
      .catch(() => setLogoUrl(defaultLogo));
  }, [token]);

  // 🔹 Fetch Bill Record + Related Data
  useEffect(() => {
    const fetchBill = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/finalbillRecord/get-bill/${billId}`,
          { headers: { token } }
        );

        if (res.data.success) {
          const fetchedBill = res.data.result.finalBillRecord;
          setBill(fetchedBill);

          // ✅ Fetch Customer
          if (
            fetchedBill.customerId &&
            typeof fetchedBill.customerId === "string"
          ) {
            const custRes = await axios.get(
              `${API_BASE_URL}/api/customer/get-customer/${fetchedBill.customerId}`,
              { headers: { token } }
            );
            if (custRes.data.success)
              setCustomer(custRes.data.result.customer || {});
          } else {
            setCustomer(fetchedBill.customerId || {});
          }

          // ✅ Fetch Project
          if (
            fetchedBill.projectId &&
            typeof fetchedBill.projectId === "string"
          ) {
            const projRes = await axios.get(
              `${API_BASE_URL}/api/project/get-project/${fetchedBill.projectId}`,
              { headers: { token } }
            );
            if (projRes.data.success)
              setProject(projRes.data.result.project || {});
          } else {
            setProject(fetchedBill.projectId || {});
          }

          // ✅ Fetch Generated By (Admin/Subadmin)
          if (fetchedBill.generatedBy) {
            try {
              // console.log("generatedBy",fetchedBill.generatedBy._id)
              const genRes = await axios.get(
                `${API_BASE_URL}/api/admin/get-subadmin/${fetchedBill.generatedBy._id}`,
                { headers: { token } }
              );
              // console.log("first", genRes)
             
              if (genRes.data.success && genRes.data.result?.subadmin) {
                setGeneratedByAdmin(genRes.data.result.subadmin);
              }
            } catch (err) {
              // console.error("Error fetching generatedBy admin:", err);
            }
          }
        } else {
          toast.error(res.data.message || "Bill not found");
        }
      } catch (error) {
        toast.error("Failed to fetch bill");
      } finally {
        setLoading(false);
      }
    };

    if (billId) fetchBill();
  }, [billId, token]);

  // ✅ PDF with Developer Details including adminName & email
  const downloadPDF = () => {
    if (!bill) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 15;

    // ---------- HEADER ----------
    doc.setFillColor(30, 60, 120);
    doc.rect(0, 0, pageWidth, 35, "F");

    if (logoUrl) {
      try {
        doc.addImage(logoUrl, "JPEG", margin, 5, 25, 20);
      } catch (e) {
        // console.log("Logo not loaded in header");
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text(
      admin.companyName || "Swaramayi Real Estates Marketing",
      margin + 35,
      15
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Real Estate Solutions", margin + 35, 23);

    let y = 45;

    // ---------- BILL INFO ----------
    doc.setFontSize(11);
    doc.setTextColor(50, 50, 50);
    doc.text(`Bill ID: ${bill._id}`, margin, y);
    doc.text(
      `Date: ${new Date(bill.createdAt).toLocaleString()}`,
      pageWidth / 2,
      y
    );
    y += 15;

    // ---------- BILL FROM / TO / DEV ----------
    autoTable(doc, {
      startY: y,
      head: [["Bill From", "Customer Details","Bill To"]],
      body: [
        [
          // ✅ Static company details
          `Swaramayi Real Estate Marketing
Madhyamgram, East Udayrajpur, Kolkata 70129
Email: srem@swaramayi.in
Website: www.swaramayi.in
Phone: 8902130791
RERA No - WBRERA/A/NOR/2025/000737`,

          // ✅ Customer details
          `${customer.customerName || "Customer Name"}\n${
            customer?.customerAddress || "Address"
          }\n${customer.customerNumber || "Phone: N/A"}`,

             `${project?.projectName}\n${
            project?.projectAddress
          }\n${project?.projectContactNumber}\n${project?.projectContactName}`

        ],
       
      ],
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [30, 60, 120], textColor: 255 },
    });

    y = doc.lastAutoTable.finalY + 10;

    // ---------- PROJECT DETAILS HEADING ----------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(30, 60, 120);
    doc.text("Project Details", margin, y);
    y += 6;

    // ---------- PROJECT DETAILS ----------
    autoTable(doc, {
      startY: y,
      head: [
        ["Project", "Type", "Flat Type", "Facing", "Amenities", "Address"],
      ],
      body: [
        [
          project.projectName || "-",
          project.projectType || "-",
          project.flatType || "-",
          project.flatFacing || "-",
          project.amenities || "-",
          project.projectAddress || "-",
        ],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [200, 200, 200], textColor: 0 },
    });

    y = doc.lastAutoTable.finalY + 10;

    // ---------- PRICE BREAKDOWN HEADING ----------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(30, 60, 120);
    doc.text("Price Breakdown", margin, y);
    y += 6;

    // ---------- PRICE BREAKDOWN ----------
    const tableData = [
      [
        "Rate per Sq.Ft",
        `${bill.areaSqft} x Rs.${bill.ratePerSqft}`,
        `Rs. ${bill.basePrice.toLocaleString("en-IN")}`,
      ],
      [
        "Amenities",
        project.amenities,
        `Rs. ${bill.amenitiesCharge.toLocaleString("en-IN")}`,
      ],
      [
        "Parking",
        "Parking Space",
        `Rs. ${(bill.parking?.total || 0).toLocaleString("en-IN")}`,
      ],
      [
        "Extra Charges",
        "Maintenance, etc.",
        `Rs. ${bill.extraCharges.toLocaleString("en-IN")}`,
      ],
      [
        "Discount",
        "Special Offer",
        `- Rs. ${bill.discount.toLocaleString("en-IN")}`,
      ],
      // [
      //   "Brokerage Fees",
      //   ` ${bill.commissionPercent.toLocaleString("en-IN")}%`,
      //   `Rs. ${(
      //     (bill.finalAgreedPrice * bill.commissionPercent) /
      //     100
      //   ).toLocaleString("en-IN")}`,
      // ],
    ];

    autoTable(doc, {
      startY: y,
      head: [["Description", "Details", "Amount"]],
      body: tableData,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [30, 60, 120], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 60 },
        2: { cellWidth: 50, halign: "right" },
      },
    });

    y = doc.lastAutoTable.finalY + 5;

    // ---------- FINAL PRICE ----------
    doc.setFillColor(0, 150, 100);
    doc.setTextColor(255, 255, 255);
    doc.rect(margin, y, pageWidth - 2 * margin, 12, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("FINAL PRICE", margin + 2, y + 8);
    doc.text(
      `Rs. ${bill.finalAgreedPrice.toLocaleString("en-IN")}`,
      pageWidth - margin - 40,
      y + 8
    );

    y += 20;

    // (AFTER FINAL PRICE)
    // ---------- COMMISSION ----------
    doc.setFillColor(0, 150, 100);
    doc.setTextColor(255, 255, 255);
    doc.rect(margin, y, pageWidth - 2 * margin, 12, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);

    // Left label
    doc.text("Brokerage Fees", margin + 2, y + 8);

    // Right side values
    const commissionPercent = `${Math.abs(bill.commissionPercent)}%`;
    const commissionAmount = `Rs. ${Math.abs(
      (bill.finalAgreedPrice * bill.commissionPercent) / 100
    ).toLocaleString("en-IN")}`;

    // Commission Percent
    doc.text(commissionPercent, pageWidth - margin - 100, y + 8);

    // Commission Amount
    doc.text(commissionAmount, pageWidth - margin - 5, y + 8, {
      align: "right",
    });

    y += 20;

    // ---------- REMARKS ----------
    if (bill.remarks) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text("Remarks:", margin, y);
      y += 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const remarksLines = doc.splitTextToSize(
        bill.remarks,
        pageWidth - 2 * margin
      );
      doc.text(remarksLines, margin, y);
    }

    // ---------- FOOTER ----------
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      "This is a computer generated quotation.",
      margin,
      doc.internal.pageSize.height - 20
    );
    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pageWidth - margin - 45,
      doc.internal.pageSize.height - 20
    );

    // Save PDF
    doc.save(`FinalBill-${bill._id}.pdf`);
  };

  return (
    <div className="bg-black/30 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="max-h-[95vh] w-[950px] overflow-y-auto rounded-lg bg-white p-10 shadow-xl">
        {loading ? (
          <p className="dark:text-black">Loading bill...</p>
        ) : !bill ? (
          <p className="text-red-500">Bill not found</p>
        ) : (
          <>
            {/* Header */}
            <div className="mb-6 flex items-center justify-between border-b pb-6">
              <div className="flex items-center gap-4">
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  className="h-16 w-16 object-contain"
                  onError={() => setLogoUrl(defaultLogo)}
                />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    {admin.companyName || "Swaramayi Real Estates Marketing"}
                  </h1>
                  <p className="text-sm text-gray-500">
                    Bill ID: {bill._id} | Date:{" "}
                    {new Date(bill.createdAt).toLocaleString()}
                  </p>
                  {/* ✅ Show Generated By
                  {generatedByAdmin && (
                    <p className="text-sm text-gray-600">
                      Generated By:{" "}
                      <span className="font-semibold">
                        {generatedByAdmin.adminName}
                      </span>
                    </p>
                  )} */}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={downloadPDF}
                  className="rounded-lg bg-blue-600 px-5 py-2 text-white shadow hover:bg-blue-700"
                >
                  Download PDF
                </button>
                <button
                  onClick={onClose}
                  className="rounded bg-red-700 px-2 py-2 text-white transition hover:bg-red-800"
                >
                  ✖
                </button>
              </div>
            </div>

            {/* Bill Info */}
            <div className="mb-6 grid grid-cols-3 gap-4 rounded-lg border bg-gray-50 p-4 text-sm">
              <p>
                <strong>Bill ID:</strong> {bill._id}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date(bill.createdAt).toLocaleString()}
              </p>
              <p>
                <strong>Generated By:</strong>{" "}
                {generatedByAdmin ? generatedByAdmin.adminName : "N/A"}
              </p>
              {/* <p>
                <strong>Valid Till:</strong>{" "}
                {bill.amountValidTill
                  ? new Date(bill.amountValidTill).toLocaleDateString()
                  : "N/A"}
              </p> */}
            </div>

            {/* Customer & Project Info */}
            <div className="mb-6 grid grid-cols-2 gap-8">
              <div className="rounded-lg border p-4">
                <h3 className="mb-3 border-b pb-1 text-lg font-semibold">
                  Customer Details
                </h3>
                <p>
                  <strong>Name:</strong> {customer.customerName || "-"}
                </p>
                <p>
                  <strong>Phone:</strong> {customer.customerNumber || "-"}
                </p>
                <p>
                  <strong>Location:</strong> {customer.locationRequired || "-"}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <h3 className="mb-3 border-b pb-1 text-lg font-semibold">
                  Project Details
                </h3>
                <p>
                  <strong>Project:</strong> {project.projectName || "-"}
                </p>
                <p>
                  <strong>Type:</strong> {project.projectType || "-"}
                </p>
                <p>
                  <strong>Flat Type:</strong> {project.flatType || "-"}
                </p>
                <p>
                  <strong>Flat Facing:</strong> {project.flatFacing || "-"}
                </p>
                <p>
                  <strong>Amenities:</strong> {project.amenities || "-"}
                </p>
                <p>
                  <strong>Address:</strong> {project.projectAddress || "-"}
                </p>
              </div>
            </div>

            {/* Charges Table */}

            <table className="mb-6 w-full border text-sm">
              <thead className="bg-gray-100">
                <tr className="text-left">
                  <th className="border p-2">Rate/Sqft</th>
                  <th className="border p-2">Area (Sqft)</th>
                  <th className="border p-2">Flat Price</th>
                  <th className="border p-2">Amenities</th>
                  <th className="border p-2">Parking</th>
                  <th className="border p-2">Extra Charges</th>
                  <th className="border p-2">Discount</th>
                  <th className="border p-2">Commission</th>
                  <th className="border p-2">Final Price</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="border p-2">
                    {Number(bill.ratePerSqft).toFixed(2)}
                  </td>
                  <td className="border p-2">{Number(bill.areaSqft)}</td>
                  <td className="border p-2">
                    {Number(bill.basePrice).toFixed(2)}
                  </td>
                  <td className="border p-2">
                    {Number(bill.amenitiesCharge).toFixed(2)}
                  </td>
                  <td className="border p-2">
                    {Number(bill.parking?.total || 0).toFixed(2)}
                  </td>
                  <td className="border p-2">
                    {Number(bill.extraCharges).toFixed(2)}
                  </td>
                  <td className="border p-2 text-red-600">
                    -{Number(bill.discount).toFixed(2)}
                  </td>
                  <td className="border p-2 font-bold text-green-700">
                    {bill.commissionPercent}%
                  </td>
                  <td className="border p-2 font-bold text-blue-700">
                    {Number(bill.finalAgreedPrice).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Remarks */}
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-2 font-semibold">Remarks</h3>
              <p className="text-gray-700">{bill.remarks || "-"}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BillView;

